import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  Typography,
  Radio,
  TableCell,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DataTable, {
  IHeadCell,
  IDataTableRow,
} from "components/Common/DataTable";
import Loading from "components/Common/Loading";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  loadZnsCampaigns,
  selectZnsCampaignResults,
  selectZnsCampaignTotalCount,
  selectZnsCampaignLoading,
  loadOlCampaigns,
  selectOlCampaignResults,
  selectOlCampaignTotalCount,
  selectOlCampaignLoading,
} from "../../store/znsCampaignSlice";
import { IZnsCampaign, IOlCampaign } from "../../types/znsCampaign";

interface SelectCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (campaign: any) => void;
  type: "OL" | "ZNS";
}

const headCells: IHeadCell[] = [
  {
    id: "radio",
    numeric: false,
    disablePadding: false,
    label: "",
    sortable: false,
  },
  {
    id: "id",
    numeric: false,
    disablePadding: false,
    label: "Campaign ID",
    sortable: true,
  },
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Campaign Name",
    sortable: true,
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Campaign Status",
    sortable: true,
  },
  {
    id: "totalUser",
    numeric: true,
    disablePadding: false,
    label: "Total User",
    sortable: false,
  },
];

const CampaignRow = (
  props: IDataTableRow & {
    selectedId?: string;
    onSelect: (id: string) => void;
  },
) => {
  const item = props.item as IZnsCampaign | IOlCampaign;
  if (!item) return null;

  return (
    <>
      <TableCell padding="checkbox">
        <Radio
          checked={props.selectedId === item.id}
          onChange={() => props.onSelect(item.id || "")}
          value={item.id}
          name="radio-buttons"
        />
      </TableCell>
      <TableCell>{item.id}</TableCell>
      <TableCell>{item.name}</TableCell>
      <TableCell>{item.status}</TableCell>
      <TableCell align="right">
        {(item as any).totalUser !== undefined
          ? (item as any).totalUser
          : "unknown"}
      </TableCell>
    </>
  );
};

const SelectCampaignDialog: React.FC<SelectCampaignDialogProps> = ({
  open,
  onClose,
  onSelect,
  type,
}) => {
  const dispatch = useAppDispatch();

  const znsResults = useAppSelector(selectZnsCampaignResults);
  const znsTotalCount = useAppSelector(selectZnsCampaignTotalCount);
  const znsLoading = useAppSelector(selectZnsCampaignLoading);

  const olResults = useAppSelector(selectOlCampaignResults);
  const olTotalCount = useAppSelector(selectOlCampaignTotalCount);
  const olLoading = useAppSelector(selectOlCampaignLoading);

  const results = type === "ZNS" ? znsResults : olResults;
  const totalCount = type === "ZNS" ? znsTotalCount : olTotalCount;
  const loading = type === "ZNS" ? znsLoading : olLoading;

  const [selectedId, setSelectedId] = useState<string>("");
  const [paging, setPagingData] = useState({ page: 1, limit: 10 });
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedId("");
    setPagingData({ page: 1, limit: 10 });
  }, [open, type]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(id);
  }, [searchInput, open]);

  useEffect(() => {
    if (!open) return;
    setPagingData((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedSearch, open]);

  useEffect(() => {
    if (open) {
      if (type === "ZNS") {
        dispatch(
          loadZnsCampaigns({
            PageNumber: paging.page,
            PageSize: paging.limit,
            ...(debouncedSearch ? { SearchTerm: debouncedSearch } : {}),
          }),
        );
      } else {
        dispatch(
          loadOlCampaigns({
            PageNumber: paging.page,
            PageSize: paging.limit,
            ...(debouncedSearch ? { SearchTerm: debouncedSearch } : {}),
          }),
        );
      }
    }
  }, [open, paging.page, paging.limit, debouncedSearch, dispatch, type]);

  const handleConfirm = () => {
    const selectedCampaign = results.find((c) => c.id === selectedId);
    if (selectedCampaign) {
      onSelect(selectedCampaign);
      setSelectedId("");
      setSearchInput("");
      setDebouncedSearch("");
      setPagingData({ page: 1, limit: 10 });
    }
  };

  const handleClose = () => {
    setPagingData({ page: 1, limit: 10 });
    setSelectedId("");
    setSearchInput("");
    setDebouncedSearch("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Select Campaign</Typography>
        <Box>
          <Button onClick={handleClose} sx={{ mr: 1 }}>
            CANCEL
          </Button>
          <Button
            variant="contained"
            disabled={!selectedId}
            onClick={handleConfirm}
          >
            SELECT
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search campaign"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {loading ? (
          <Box sx={{ p: 4 }}>
            <Loading />
          </Box>
        ) : (
          <DataTable
            hideToolbar
            title=""
            headCells={headCells}
            rows={results}
            paging={{
              total: totalCount,
              pages: Math.ceil(totalCount / paging.limit) || 1,
              page: paging.page - 1,
              limit: paging.limit,
            }}
            onPageChange={(page) =>
              setPagingData((prev) => ({ ...prev, page: page + 1 }))
            }
            expandablePagination
          >
            <CampaignRow selectedId={selectedId} onSelect={setSelectedId} />
          </DataTable>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SelectCampaignDialog;
